/**
 * ============================================================================
 * MEMORY ADAPTER FACTORY
 * ============================================================================
 *
 * MVP ADAPTER SELECTION GUIDE
 * ---------------------------
 *
 * PRODUCTION MVP PATH:
 *   Env: NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM=true
 *        NEXT_PUBLIC_VERCEL_ENV=production
 *   User: Authenticated
 *   Result: SupabaseAdapter (full persistence)
 *
 * PREVIEW/STAGING PATH:
 *   Env: NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM=true
 *        NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN=true
 *        NEXT_PUBLIC_VERCEL_ENV=preview
 *   User: Authenticated
 *   Result: DualWriteAdapter (writes to both, reads from Supabase first)
 *
 * DEMO PATH:
 *   Env: NEXT_PUBLIC_BIZIQ_DEMO_MODE=true
 *   Result: DemoAdapter (read-only pre-seeded data)
 *
 * DEVELOPMENT/FALLBACK PATH:
 *   Default when no flags set or user not authenticated
 *   Result: SessionAdapter (browser session storage)
 *
 * ============================================================================
 * ENVIRONMENT FLAGS (MVP MINIMAL SET)
 * ============================================================================
 *
 * NEXT_PUBLIC_BIZIQ_DEMO_MODE         - Enable demo mode with pre-seeded data
 * NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM   - Enable Supabase-backed memory
 * NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN- Enable dual-write in preview
 * NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE- (Future) Full Supabase mode override
 * NEXT_PUBLIC_VERCEL_ENV              - Environment detection (preview/production)
 * NEXT_PUBLIC_ENABLE_COMPARISON_DEMO  - Enable comparison demo feature
 *
 * ============================================================================
 */

import type { IStorageAdapter } from "./storage-adapter"
import { getSessionAdapter } from "./adapters/session-adapter"
import { getDemoAdapter } from "./adapters/demo-adapter"
import { getSupabaseAdapter } from "./adapters/supabase-adapter"
import { getDualWriteAdapter, isDualWriteModeEnabled } from "./adapters/dual-write-adapter"
import { createClient } from "@/lib/supabase/client"

export type AdapterMode = "session" | "demo" | "supabase" | "dual-write"

interface AdapterOptions {
  isDemoMode?: boolean
  isAuthenticated?: boolean
}

// === Environment Detection Helpers ===

function isSupabaseModeEnabled(): boolean {
  if (typeof window === "undefined") return false
  return process.env.NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM === "true"
}

function isProductionEnvironment(): boolean {
  if (typeof window === "undefined") return false
  return process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
}

/**
 * Check if pure Supabase Brain mode should be enabled.
 * Currently gated behind NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE flag.
 * Will be enabled after dual-write validation is complete.
 */
export function isSupabaseBrainEnabled(): boolean {
  if (typeof window === "undefined") return false
  const fullModeFlag = process.env.NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE === "true"
  if (!fullModeFlag) return false
  return isSupabaseModeEnabled() && isProductionEnvironment()
}

// === Main Adapter Factory ===

/**
 * Get the appropriate storage adapter based on current context.
 * See file header for decision tree documentation.
 */
export function getStorageAdapter(options: AdapterOptions = {}): IStorageAdapter {
  const { isDemoMode = false, isAuthenticated = false } = options

  const isStaging = process.env.NEXT_PUBLIC_VERCEL_ENV !== "production"
  if (isStaging && typeof window !== "undefined") {
    const lastCheckKey = "__adapter_auth_check_time"
    const now = Date.now()
    const lastCheck = Number.parseInt(window.sessionStorage.getItem(lastCheckKey) || "0", 10)

    // Only check if more than 60 seconds have passed
    if (now - lastCheck > 60000) {
      window.sessionStorage.setItem(lastCheckKey, now.toString())

      const supabase = createClient()
      supabase.auth.getSession().then(({ data: { session } }) => {
        const actuallyAuthenticated = !!session?.user

        if (actuallyAuthenticated !== isAuthenticated && lastCheck > 0) {
          console.warn(
            "[Memory] Auth state mismatch detected",
            `passed=${isAuthenticated} actual=${actuallyAuthenticated}`,
            "This indicates adapter selection is using stale auth state.",
            "This check runs max once per minute.",
          )
        }

        console.log("[Memory] Adapter Selection Inputs:", {
          isDemoMode,
          isAuthenticatedParam: isAuthenticated,
          actualSession: actuallyAuthenticated,
          sessionUserId: session?.user?.id?.substring(0, 8) || null,
          isProduction: isProductionEnvironment(),
          isSupabaseEnabled: isSupabaseModeEnabled(),
          isDualWriteEnabled: isDualWriteModeEnabled(),
        })
      })
    }
  }

  // PATH 1: Demo mode (highest priority, read-only)
  if (isDemoMode) {
    console.log("[Memory] adapter=demo reason=demo_mode_enabled")
    return getDemoAdapter()
  }

  // PATH 2: Production + Supabase mode + authenticated
  if (isProductionEnvironment() && isSupabaseModeEnabled() && isAuthenticated) {
    console.log("[Memory] adapter=supabase reason=production_authenticated")
    return getSupabaseAdapter()
  }

  // PATH 3: Preview/staging with dual-write enabled + authenticated
  if (isDualWriteModeEnabled() && isAuthenticated) {
    console.log("[Memory] adapter=dual-write reason=preview_dual_write")
    return getDualWriteAdapter()
  }

  // PATH 4: Development with Supabase mode + authenticated
  if (isSupabaseModeEnabled() && isAuthenticated) {
    console.log("[Memory] adapter=supabase reason=dev_supabase_mode")
    return getSupabaseAdapter()
  }

  // PATH 5: Default fallback - session storage
  if (isAuthenticated && isStaging) {
    console.warn("[Memory] adapter=session reason=default_fallback WARNING=authenticated_user_using_session_storage")
  } else {
    console.log("[Memory] adapter=session reason=default_fallback")
  }
  return getSessionAdapter()
}

/**
 * Get the current adapter mode for debugging/UI display
 */
export function getAdapterMode(options: AdapterOptions = {}): AdapterMode {
  const { isDemoMode = false, isAuthenticated = false } = options

  if (isDemoMode) return "demo"
  if (isProductionEnvironment() && isSupabaseModeEnabled() && isAuthenticated) return "supabase"
  if (isDualWriteModeEnabled() && isAuthenticated) return "dual-write"
  if (isSupabaseModeEnabled() && isAuthenticated) return "supabase"
  return "session"
}

/**
 * Debug helper: Log current adapter selection logic
 * Only use in development/preview environments
 */
export function debugAdapterSelection(options: AdapterOptions = {}): void {
  if (isProductionEnvironment()) {
    console.log("[Memory] Debug disabled in production")
    return
  }

  const { isDemoMode = false, isAuthenticated = false } = options

  console.group("[Memory] Adapter Selection Debug")
  console.log("isDemoMode:", isDemoMode)
  console.log("isAuthenticated:", isAuthenticated)
  console.log("NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM:", process.env.NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM)
  console.log("NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN:", process.env.NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN)
  console.log("NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE:", process.env.NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE)
  console.log("NEXT_PUBLIC_VERCEL_ENV:", process.env.NEXT_PUBLIC_VERCEL_ENV)
  console.log("Selected mode:", getAdapterMode(options))
  console.groupEnd()
}

/**
 * ============================================================================
 * MEMORY ADAPTER FACTORY
 * ============================================================================
 *
 * MVP PILOT CONFIGURATION (RECOMMENDED)
 * -------------------------------------
 * For production pilot deployment:
 *
 *   NEXT_PUBLIC_BIZIQ_DEMO_MODE=false
 *   NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM=true
 *   NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN=false
 *   NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE=false  (POST_MVP - not needed for pilot)
 *   NEXT_PUBLIC_VERCEL_ENV=production
 *   NEXT_PUBLIC_ENABLE_COMPARISON_DEMO=true
 *
 * This configuration routes authenticated users to SupabaseAdapter.
 *
 * DEMO MODE CONFIGURATION
 * -----------------------
 * For demo/sales presentations:
 *
 *   NEXT_PUBLIC_BIZIQ_DEMO_MODE=true
 *   BIZIQ_DEMO_USER_ID=demo-user-xxx
 *   BIZIQ_DEMO_TENANT_ID=demo-tenant-xxx
 *
 * This isolates all operations to read-only pre-seeded data.
 * No risk of pilot tenant data corruption.
 *
 * ============================================================================
 * ADAPTER SELECTION PATHS
 * ============================================================================
 *
 * PATH 1 - DEMO MODE (highest priority):
 *   Condition: NEXT_PUBLIC_BIZIQ_DEMO_MODE=true
 *   Adapter: DemoAdapter (read-only pre-seeded data)
 *   Isolation: Complete - uses BIZIQ_DEMO_USER_ID / BIZIQ_DEMO_TENANT_ID
 *
 * PATH 2 - PRODUCTION AUTHENTICATED:
 *   Condition: VERCEL_ENV=production + USE_NEW_MEMORY_SYSTEM=true + auth
 *   Adapter: SupabaseAdapter (full persistence)
 *   Isolation: RLS policies enforce tenant/user boundaries
 *
 * PATH 3 - PREVIEW DUAL-WRITE:
 *   Condition: VERCEL_ENV=preview + BIZIQ_USE_SUPABASE_BRAIN=true + auth
 *   Adapter: DualWriteAdapter (writes both, reads Supabase first)
 *   Purpose: Validate Supabase RLS before production rollout
 *
 * PATH 4 - DEVELOPMENT SUPABASE:
 *   Condition: USE_NEW_MEMORY_SYSTEM=true + auth (non-production)
 *   Adapter: SupabaseAdapter
 *   Purpose: Local development with Supabase
 *
 * PATH 5 - FALLBACK:
 *   Condition: Default when no flags set or user not authenticated
 *   Adapter: SessionAdapter (browser session storage)
 *   Purpose: Unauthenticated onboarding, development
 *
 * ============================================================================
 * ENVIRONMENT FLAGS REFERENCE
 * ============================================================================
 *
 * MVP_CORE FLAGS:
 *   NEXT_PUBLIC_BIZIQ_DEMO_MODE         - Enable demo mode with pre-seeded data
 *   NEXT_PUBLIC_USE_NEW_MEMORY_SYSTEM   - Enable Supabase-backed memory
 *   NEXT_PUBLIC_ENABLE_COMPARISON_DEMO  - Enable comparison demo feature
 *   NEXT_PUBLIC_VERCEL_ENV              - Environment detection (auto-set by Vercel)
 *
 * MVP_SUPPORT FLAGS (preview/testing):
 *   NEXT_PUBLIC_BIZIQ_USE_SUPABASE_BRAIN - Enable dual-write in preview
 *
 * POST_MVP FLAGS (not needed for pilot):
 *   NEXT_PUBLIC_SUPABASE_BRAIN_FULL_MODE - Full Supabase mode override
 *
 * ============================================================================
 */
